import { ButtonGroup, PrimaryButton } from "@digipost/dds-buttons-react";
import { Checkbox } from "@digipost/dds-checkbox-react";
import { Heading } from "@digipost/dds-heading-react";
import Infobox from "@digipost/dds-infobox-react";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";

import digitalDealIllustration from "../../../assets/images/illustrations/digitalavtale.svg";
import mobileWithDocumentsIllustration from "../../../assets/images/illustrations/mobilmeddokumenter.svg";
import {
  SiteImproveActions,
  SiteImproveCategories,
  siteImproveEvent,
} from "../../analytics/SiteImprove";
import { ReduxState } from "../../infrastructure/redux/state";
import Translated from "../../infrastructure/Translated";
import { useTranslate } from "../../infrastructure/useTranslate";
import RegisterFlowStepper from "../RegisterFlowStepper";
import styles from "./Terms.module.css";

const Terms: FC = () => {
  const translate = useTranslate();
  const params = useSelector((state: ReduxState) => state.app.params);
  const location = useLocation();
  const erBehandlerflytType1 =
    params.mode === "behandler" && params.integrationType !== "2";
  const erSigneringsFlyt = params.mode === "signering";

  const selectedIntegrationPartner = useSelector(
    (state: ReduxState) => state.org.userInput?.selectedIntegrationPartner,
  );

  const integrationPartnerName =
    selectedIntegrationPartner?.name ??
    translate("generic.the_system_provider");

  const [godkjent, setGodkjent] = useState(false);
  const [showRegisterError, setShowRegisterError] = useState(false);
  const navigate = useNavigate();

  const handleCheckboxChange = (): void => {
    setGodkjent((prev) => !prev);
    setShowRegisterError(false);
  };

  const handleButtonClick = (): void => {
    if (godkjent) {
      if (location.pathname.includes("manuell")) {
        navigate("../skjema");
      } else {
        navigate("../kontodetaljer");
      }
    } else {
      setShowRegisterError(true);
    }
  };

  useEffect(() => {
    document.title = translate("terms.html_title");

    if (erSigneringsFlyt) {
      siteImproveEvent({
        category: SiteImproveCategories.SIGNERING_VIRKSOMHET,
        action: SiteImproveActions.FROM_SIGNERING,
      });
    }
  }, [erSigneringsFlyt, translate]);

  return (
    <div>
      <div>
        <Heading level={1} size="large">
          <Translated textKey="terms.title" />
        </Heading>

        <RegisterFlowStepper currentStep="TERMS" />

        {erBehandlerflytType1 && (
          <>
            <p>
              <Translated
                textKey="terms.partner.info.one"
                params={{ partnerName: integrationPartnerName }}
              />
            </p>
            <p>
              <Translated
                textKey="terms.partner.info.two"
                params={{ partnerName: integrationPartnerName }}
              />
            </p>
            <p>
              <Translated
                textKey="terms.partner.info.three"
                params={{ partnerName: integrationPartnerName }}
              />
            </p>
          </>
        )}

        <section>
          <Heading level={2} size="small">
            <Translated textKey="terms.digipost_and_signering.header" />
          </Heading>
          <p>
            <Translated textKey="terms.digipost_and_signering.infotext" />
          </p>
          <div className={styles.servicesWrapper}>
            <div className={styles.service}>
              <img
                src={mobileWithDocumentsIllustration}
                alt={translate("terms.selling_point.digipost.alt_text")}
              />
              <Heading level={3} size="xsmall">
                Digipost
              </Heading>
              <p>
                <Translated textKey="terms.selling_point.digipost" />
              </p>
            </div>
            <div className={styles.service}>
              <img
                src={digitalDealIllustration}
                alt={translate("terms.selling_point.signering.alt_text")}
              />
              <Heading level={3} size="xsmall">
                Posten signering
              </Heading>
              <p>
                <Translated textKey="terms.selling_point.signering" />
              </p>
            </div>
          </div>
        </section>

        <section>
          <Heading level={2} size="small">
            <Translated textKey="terms.subsection.title" />
          </Heading>
          <div>
            <div className={styles.acceptCheckbox}>
              <Checkbox
                checked={godkjent}
                onChange={handleCheckboxChange}
                data-testid="bedrift-accept-terms-checkbox"
              >
                <Translated textKey="terms.accept_terms" allowHtml={true} />
              </Checkbox>
            </div>

            {showRegisterError && (
              <Infobox
                variant="warning"
                level={3}
                title={translate("terms.must_accept_terms")}
                className={styles.warningText}
              />
            )}

            <ButtonGroup>
              <PrimaryButton
                data-testid="bedrift-accept-terms-next-button"
                onClick={handleButtonClick}
              >
                <Translated textKey="generic.continue" />
              </PrimaryButton>
            </ButtonGroup>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Terms;
